/**
 * **Which raves this device keeps for offline — known on EVERY MyRave address, with no signal** (the QA walk,
 * w-offline-per-edition, 2026-11-02: OZORA kept offline; online, the user attended Tomorrowland and it downloaded;
 * offline again, OZORA's menu did not list Tomorrowland to switch back).
 *
 * Every address keeps its own copy of My raves (`my-raves-<account>`, `lib/api.ts`), so OZORA's copy was from before
 * Tomorrowland was attended. A platform-domain cookie is the one store every address shares and reads offline:
 * `myrave_kept_raves` holds the account's attended raves that are not over by two days (`pastRaves.ts`, the rule that
 * decides what is kept), soonest first, at most `MAX_KEPT` — just what a menu row and a link need. Written whenever
 * any address hears My raves from the server; read only for the same account and only when the server could not be
 * asked (`fromDevice`), to add the raves that address's own copy is missing. Signing out clears it.
 */
import { readPlatformCookie, writePlatformCookie, clearPlatformCookie } from './signOut';
import { overForOffline } from '@/offline/pastRaveRule';
import type { MyFestival } from './api';

export const KEPT_RAVES_COOKIE = 'myrave_kept_raves';
export const MAX_KEPT = 6;
const MAX_BYTES = 3000;
const MAX_AGE = 400 * 24 * 60 * 60;

interface KeptRow { s: string; e: string; i: string; n: string; f: string; g: string | null; x: string | null; z: string }
interface KeptValue { a: string; r: KeptRow[] }

const cut = (s: string | null | undefined) => (s ?? '').slice(0, 40);

/** The raves kept for offline, by the rule: attended, not over by two days, soonest first. */
export function keptFor(festivals: readonly MyFestival[], now = Date.now()): MyFestival[] {
  const at = (s: string | null) => (s ? Date.parse(s) : Infinity);
  return festivals
    .filter((f) => f.attending !== false && !overForOffline(f.endsAt, now))
    .sort((a, b) => at(a.gatesOpenAt) - at(b.gatesOpenAt) || String(a.editionId).localeCompare(String(b.editionId)))
    .slice(0, MAX_KEPT);
}

/** Note the server's My raves for every address on this device. Never throws: it rides on the answer. */
export function noteKeptRaves(accountId: string, festivals: readonly MyFestival[], now = Date.now()): void {
  try { writeKept(accountId, festivals, now); } catch { /* a cookie that cannot be written: nothing shared */ }
}

function writeKept(accountId: string, festivals: readonly MyFestival[], now: number): void {
  const rows: KeptRow[] = keptFor(festivals, now).filter((f) => f.subdomain && f.editionId).map((f) => ({
    s: f.subdomain, e: f.editionSlug ?? '', i: f.editionId, n: cut(f.editionName), f: cut(f.festivalName),
    g: f.gatesOpenAt ?? null, x: f.endsAt ?? null, z: f.timezone ?? 'UTC',
  }));
  let value = '';
  while (rows.length) {
    value = encodeURIComponent(JSON.stringify({ a: accountId, r: rows } satisfies KeptValue));
    if (value.length <= MAX_BYTES) break;
    rows.pop();
  }
  if (!rows.length) { clearPlatformCookie(KEPT_RAVES_COOKIE); return; }
  writePlatformCookie(KEPT_RAVES_COOKIE, value, MAX_AGE);
}

/** The kept raves this device noted for `accountId` — as My raves rows (no pictures, no counts). */
export function keptRaves(accountId: string | null, now = Date.now()): MyFestival[] {
  if (!accountId) return [];
  const raw = readPlatformCookie(KEPT_RAVES_COOKIE);
  if (!raw) return [];
  let v: KeptValue;
  try { v = JSON.parse(decodeURIComponent(raw)) as KeptValue; } catch { return []; }
  if (!v || v.a !== accountId || !Array.isArray(v.r)) return [];
  return v.r
    .filter((r) => r && typeof r.s === 'string' && typeof r.i === 'string' && !overForOffline(r.x, now))
    .map((r) => ({
      editionId: r.i, festivalId: '', editionName: r.n, editionSlug: r.e, festivalName: r.f, subdomain: r.s,
      gatesOpenAt: r.g, endsAt: r.x, timezone: r.z, attendingSince: '', attending: true, venue: null,
      country: null, logoUrl: null, coverUrl: null, saved: 0, clashes: 0, days: 0, plannedMinutes: 0,
      actCount: 0, stageCount: 0,
    }));
}

/** This address's own copy of My raves, with the kept raves it is missing added. */
export function withKeptRaves(festivals: readonly MyFestival[], accountId: string | null, now = Date.now()): MyFestival[] {
  const have = new Set(festivals.map((f) => f.editionId));
  const missing = keptRaves(accountId, now).filter((k) => !have.has(k.editionId));
  return missing.length ? [...festivals, ...missing] : [...festivals];
}

export function forgetKeptRaves(): void {
  clearPlatformCookie(KEPT_RAVES_COOKIE);
}
